import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Hourglass, CheckCircle, Info } from '@phosphor-icons/react';
import HaleConsoleLayout from '../../components/hale/HaleConsoleLayout';
import { T, FONT, PageHeading, ConsoleCard, Eyebrow, StatusPill, EmptyState, Spinner } from './_theme';

const API = process.env.REACT_APP_BACKEND_URL;

/* Scientific Review — brand-facing view derived from the application's
 * review status + decision (no separate review table). */

export default function HaleReviewsPage() {
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [none, setNone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/hale-marketplace/applications/me`, { withCredentials: true });
        setApp(r.data);
      } catch (e) {
        if (e?.response?.status === 401) { navigate('/hale/login', { replace: true }); return; }
        if (e?.response?.status === 404) setNone(true);
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  const status = app?.status;
  const decision = app?.decision || null;
  const pending = status === 'pending_review';
  const completed = ['approved', 'approved_with_recommendations', 'not_approved'].includes(status);
  const rfis = status === 'pending_revision'
    ? ['The Hale review team has requested updates to your submission. Please revise and resubmit.']
    : (Array.isArray(decision?.recommendations) ? decision.recommendations.map((r) => (typeof r === 'string' ? r : r.text || '')) : []);

  const Section = ({ icon: Icon, title, count, children }) => (
    <ConsoleCard>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} weight="bold" style={{ color: T.RED }} />
        <p className="text-[15px] font-bold" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>{title}</p>
        <span className="ml-auto text-[12px] font-mono" style={{ color: T.MUTED }}>{count}</span>
      </div>
      {children}
    </ConsoleCard>
  );

  return (
    <HaleConsoleLayout>
      <PageHeading title="Scientific Review" sub="Independent safety & regulatory review of your products, powered by ToxIntel + Aᴳ." testid="hale-reviews-title" />

      {loading && <Spinner label="Loading review status…" />}

      {!loading && none && (
        <EmptyState icon={ShieldCheck} title="No reviews yet" testid="hale-reviews-empty"
          desc="Once you submit a Hale application, your scientific review status will appear here."
          ctaLabel="Begin application" ctaTo="/hale/apply" />
      )}

      {!loading && app && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="hale-reviews-grid">
          <Section icon={Hourglass} title="Pending Reviews" count={pending ? 1 : 0}>
            {pending ? (
              <div className="flex items-center gap-2">
                <StatusPill status="pending_review" />
                <span className="text-[13px]" style={{ color: T.SOFT }}>Your submission is with the Hale review team.</span>
              </div>
            ) : <p className="text-[13px]" style={{ color: T.MUTED }}>No pending reviews.</p>}
          </Section>

          <Section icon={CheckCircle} title="Completed Reviews" count={completed ? 1 : 0}>
            {completed ? (
              <div>
                <StatusPill status={status} />
                {decision?.decided_at && <p className="text-[12px] mt-2" style={{ color: T.MUTED }}>Decided {new Date(decision.decided_at).toLocaleDateString()}</p>}
                {decision?.reviewer_notes && <p className="text-[13px] mt-2 leading-relaxed" style={{ color: T.SOFT }}>{decision.reviewer_notes}</p>}
              </div>
            ) : <p className="text-[13px]" style={{ color: T.MUTED }}>No completed reviews yet.</p>}
          </Section>

          <Section icon={Info} title="Requests for Information" count={rfis.length}>
            {rfis.length > 0 ? (
              <ul className="space-y-2">
                {rfis.map((r, i) => <li key={i} className="text-[13px] flex gap-2" style={{ color: T.SOFT }}><span style={{ color: T.RED }}>•</span> {r}</li>)}
              </ul>
            ) : <p className="text-[13px]" style={{ color: T.MUTED }}>No open requests.</p>}
          </Section>
        </div>
      )}
    </HaleConsoleLayout>
  );
}
